import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Dropdown,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import Settings01 from "@untitled-ui/icons-react/build/esm/Settings01";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

const STATUS_COLORS = {
  ACTIVE: { bg: "#ECFDF3", color: "#027A48" },
  PENDING: { bg: "#FFFAEB", color: "#B54708" },
  MATURED: { bg: "#EFF8FF", color: "#175CD3" },
  REJECTED: { bg: "#FEF3F2", color: "#B42318" },
  CLOSED: { bg: "#F2F4F7", color: "#344054" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 99,
        fontSize: 13,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {status}
    </span>
  );
};

const ViewTreasuryBillContract = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("PENDING");
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (id) fetchContractDetail(id);
  }, [id]);

  const fetchContractDetail = async (contractId) => {
    try {
      setIsLoading(true);
      // const res = await BOM.SendReq(apiConfig.getTBContractById, { id: contractId });
      const data = {
        id: 1,
        accountNo: "TB-2026-001",
        clientId: 1,
        productId: 1,
        status: "ACTIVE",
        faceValue: 50_000_000,
        purchasePrice: 49_250_000,
        issueDate: "2026-02-24",
        maturityDate: "2026-05-25",
        tenor: 91,
        discountRate: 12.5,
        settlementId: 1,
        tradeId: 1,
      };
      setStatus(data.status);
      form.setFieldsValue({
        ...data,
        issueDate: dayjs(data.issueDate),
        maturityDate: dayjs(data.maturityDate),
      });
    } catch (error) {
      BOM.AlertMsg(cnt.DANGER, error?.message || "Failed to load contract.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Direct API actions (no page) ─────────────────────────────────────────
  const handleCloseContract = async () => {
    try {
      setIsLoading(true);
      // await BOM.SendReq(apiConfig.closeContract, { id });
      console.log("Close Contract — id:", id);
      BOM.AlertMsg(cnt.SUCCESS, "Contract closed successfully.");
      navigate(-1);
    } catch (error) {
      BOM.AlertMsg(cnt.DANGER, error?.message || "Failed to close contract.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetTransactions = async () => {
    try {
      setIsLoading(true);
      // const res = await BOM.SendReq(apiConfig.getContractTransactions, { id });
      console.log("Get Contract Transactions — id:", id);
      // Handle / display res.data as needed (e.g. navigate to transactions sub-page)
    } catch (error) {
      BOM.AlertMsg(cnt.DANGER, error?.message || "Failed to get transactions.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetTransactionDetail = async () => {
    try {
      setIsLoading(true);
      // const res = await BOM.SendReq(apiConfig.getTransactionDetail, { id });
      console.log("Get Transaction Detail — id:", id);
      // Handle / display res.data as needed
    } catch (error) {
      BOM.AlertMsg(
        cnt.DANGER,
        error?.message || "Failed to get transaction detail.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = ({ key }) => {
    switch (key) {
      case "approve":
        navigate(`/treasury-bill-contract/approve`);
        break;
      case "reject":
        navigate(`/treasury-bill-contract/reject`);
        break;
      case "disburse":
        navigate(`/treasury-bill-contract/disburse`);
        break;
      case "premature_redemption":
        navigate(`/treasury-bill-contract/premature-redemption`);
        break;
      case "rollover":
        navigate(`/treasury-bill-contract/rollover`);
        break;
      case "close":
        handleCloseContract();
        break;
      case "get_transactions":
        handleGetTransactions();
        break;
      case "get_transaction_detail":
        handleGetTransactionDetail();
        break;
      default:
        break;
    }
  };

  const actionMenuItems = [
    { key: "approve", label: "Approve Contract" },
    { key: "reject", label: "Reject Contract" },
    { type: "divider" },
    { key: "disburse", label: "Disburse Contract" },
    { key: "premature_redemption", label: "Premature Redemption" },
    { key: "rollover", label: "Rollover Contract" },
    { type: "divider" },
    {
      key: "close",
      label: <span style={{ color: "#B42318" }}>Close Contract</span>,
    },
    { type: "divider" },
    { key: "get_transactions", label: "Get Contract Transactions" },
    { key: "get_transaction_detail", label: "Get Transaction Detail" },
  ];

  const ds = { height: 44, background: "#F9FAFB", cursor: "not-allowed" };

  const LabelWithTip = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  return (
    <MainLayout pageItems={staticConfig.newtreasuryBillContract}>
      {/* <TopContent pageTitle={labelConfig.treasuryBillContract} /> */}

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            {/* Header */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <h3 style={{ margin: 0, fontWeight: 600 }}>
                  Treausry Bill Contract Details
                </h3>
                {/* <StatusBadge status={status} /> */}
              </Col>
              <Col>
                <Dropdown
                  menu={{ items: actionMenuItems, onClick: handleAction }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    type="button"
                    style={{
                      height: 44,
                      padding: "0 20px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Settings01 width={16} height={16} color="#344054" />
                    Actions
                    <ChevronDown width={16} height={16} color="#667085" />
                  </button>
                </Dropdown>
              </Col>
            </Row>

            <Form layout="vertical" form={form}>
              {/* Account No & Client ID */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Account No"
                        tip="The unique account number for this contract."
                      />
                    }
                    name="accountNo"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Client ID"
                        tip="The client this contract belongs to."
                      />
                    }
                    name="clientId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Product ID & Trade ID */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Product ID"
                        tip="The treasury product linked to this contract."
                      />
                    }
                    name="productId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Trade ID"
                        tip="The trade this contract is linked to."
                      />
                    }
                    name="tradeId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Settlement ID & Face Value */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Settlement ID"
                        tip="The settlement record associated with this contract."
                      />
                    }
                    name="settlementId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Face Value"
                        tip="The nominal / par value of the treasury bill."
                      />
                    }
                    name="faceValue"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Purchase Price & Discount Rate */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Purchase Price"
                        tip="The actual price paid for the treasury bill."
                      />
                    }
                    name="purchasePrice"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Discount Rate (%)"
                        tip="The annualised discount rate applied to this treasury bill."
                      />
                    }
                    name="discountRate"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      precision={2}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Issue Date & Maturity Date */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Issue Date"
                        tip="The date the treasury bill was issued."
                      />
                    }
                    name="issueDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Maturity Date"
                        tip="The date the treasury bill matures."
                      />
                    }
                    name="maturityDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Tenor */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Tenor (Days)"
                        tip="The number of days from issue to maturity."
                      />
                    }
                    name="tenor"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      precision={0}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Back
                  </button>
                </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({ rightBtn: state.RightSideBtn });
export default connect(mapStateToProps, { rightAction })(
  ViewTreasuryBillContract,
);
